import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Row } from "react-bootstrap";
import { PencilIcon, SearchIcon } from '@primer/octicons-react'
import LocPickerDialog from './LocPickerDialog';
import { axiosAuth_LEASE_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';
import { isNaturalNumber } from '../utils/jsUtils';


const FloorAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [floorAttrs, setFloorAttrs] = useState({
    id: '',
    name: '',
    description: '',
    type: '',
    grossFloorArea: '',
    saleableFloorArea: '',
    sortSequenceNum: '',
    floorLocCode: '',
    floorLocId: '',
    status: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: ''
  });

  const statusOptions = [
    { key: "VACANT", value: "VACANT" },
    { key: "LEASED", value: "LEASED" }
  ];


  const [currentFloorAttrs, setCurrentFloorAttrs] = useState({});

  // Reserve for potential enhancement
  //const [floorTypeList, setFloorTypeList] = useState([]);

  const [showLocPickerDialog, setShowLocPickerDialog] = useState(false);

  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setFloorAttrs(props.objDetails);
      setCurrentFloorAttrs(props.objDetails);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,             setErrMsg]             = useState('');
  const [errMsg_Update,      setErrMsg_Update]      = useState('');

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setFloorAttrs({ ...floorAttrs, [e.target.name]: e.target.value })
  }

  const onStatusSelect = (key, event) => {
    setFloorAttrs({ ...floorAttrs, status: key })
  };

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setFloorAttrs(currentFloorAttrs);

    setEditMode(false);
    setErrMsg_Update('');
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate form inputs
    if (floorAttrs.name.trim().length === 0) {
      setErrMsg_Update(t("I18N_ERR_Need_floor_name"));
    } else if ((floorAttrs.name != null) && (floorAttrs.name.length > 100)) {
      setErrMsg_Update(t("I18N_ERR_Max_len_name") + 100);
    } else if ((floorAttrs.nameLang2 != null) && (floorAttrs.nameLang2.length > 100)) {
      setErrMsg_Update(t("I18N_ERR_Max_len_name_no_col") + " (" + t("custom:I18N_Language_2") + "): " + 100);
    } else if ((floorAttrs.nameLang3 != null) && (floorAttrs.nameLang3.length > 100)) {
      setErrMsg_Update(t("I18N_ERR_Max_len_name_no_col") + " (" + t("custom:I18N_Language_3") + "): " + 100);
    } else if (!floorAttrs.description || floorAttrs.description.trim().length === 0) {
      setErrMsg_Update(t("I18N_ERR_Need_description"));
    } else if ((floorAttrs.description != null) && (floorAttrs.description.length > 200)) {
      setErrMsg_Update(t("I18N_ERR_Max_len_desc") + 200);
/*
    } else if (floorAttrs.type === '') {
      setErrMsg_Update(t("I18N_ERR_Need_type"));
    } else if ((floorAttrs.type != null) && (floorAttrs.type.length > 20)) {
      setErrMsg_Update(t("I18N_ERR_Max_len_type") + 20);
*/
    } else if (floorAttrs.grossFloorArea && !isNaturalNumber(floorAttrs.grossFloorArea)) {
      setErrMsg_Update(t("I18N_ERR_Validate_GFA"));
      return;
    } else if (floorAttrs.saleableFloorArea && !isNaturalNumber(floorAttrs.saleableFloorArea)) {
      setErrMsg_Update(t("I18N_ERR_Validate_SFA"));
      return;
    } else if (floorAttrs.sortSequenceNum && !isNaturalNumber(floorAttrs.sortSequenceNum)) {
      setErrMsg_Update(t("I18N_ERR_Validate_Sort_seq_num"));
      return;
    } else {
      setErrMsg_Update('');
      updateFloor();
    }
  }

  const updateFloor = () => {

    const reqBody = floorAttrs;

    if (reqBody.location === '') {
      reqBody.location = '';
      reqBody.locationId = '';
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_LEASE_MGMT_API.put("prop-floors/" + floorAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          refreshFloorAttrs(floorAttrs.id);

          // Restore UI to viewing mode
          setEditMode(false);

          setErrMsg_Update('');
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg_Update(t(translateAPIError(error)));
        }
      }
    });

  }


  const refreshFloorAttrs = (floorId) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_LEASE_MGMT_API.get("prop-floors/" + floorId);

        resolve(res.data);

        if (res.status === 200) {

          setFloorAttrs(res.data.data[0]);
          setCurrentFloorAttrs(res.data.data[0]);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  // Location select dialog stuff

  const handleLocPicker = (e) => {
    setShowLocPickerDialog(true);
  }

  function handleLocPickerClose() {
    setShowLocPickerDialog(false);
  }

  function handleLocSelect(location) {
/* ##### If I do this, the locationId is NOT set.  Why??? #####
    setFloorAttrs({ ...floorAttrs, locationId: location.id });
    setFloorAttrs({ ...floorAttrs, location:   location.code });
*/
    // Deep copy
    let newFloorAttr = JSON.parse (JSON.stringify(floorAttrs));
    newFloorAttr.floorLocId   = location.id;
    newFloorAttr.floorLocCode = location.code;

    setFloorAttrs(newFloorAttr);

    setShowLocPickerDialog(false);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', height:'600px', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_Prop_Floor_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Prop_floor_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Name")}: *</Col>
                  <Col>{t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ")"}:</Col>
                  <Col>{t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ")"}:</Col>
                  <Col>{t("I18N_Description")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="name" value={floorAttrs.name}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="nameLang2" value={floorAttrs.nameLang2}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="nameLang3" value={floorAttrs.nameLang3}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="description" value={floorAttrs.description}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Fl_loc_code")}: *</Col>
                  <Col>{t("I18N_GFA")}:</Col>
                  <Col>{t("I18N_SFA")}:</Col>
                  <Col>{t("I18N_Sort_seq_num")}:</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input readOnly className={classes.admin_pages__field_editable} style={{width:'80%'}} name="location" value={floorAttrs.floorLocCode}
                      onChange={(e) => onAttrsInputChange(e)} />
                    <span onClick={handleLocPicker} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={14} />
                    </span>
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="grossFloorArea" value={floorAttrs.grossFloorArea}
                           onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="saleableFloorArea" value={floorAttrs.saleableFloorArea}
                           onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="sortSequenceNum" value={floorAttrs.sortSequenceNum}
                           onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Status")}: </Col>
                  <Col></Col>
                  <Col></Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={ !floorAttrs.status ? t("common:I18N_DROPDOWN_PROMPT") : t("I18N_" + floorAttrs.status) }
                      onSelect={onStatusSelect}>
                      {statusOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col></Col>
                  <Col></Col>
                  <Col></Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg_Update}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Name")}:</Col>
                <Col>{t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ")"}:</Col>
                <Col>{t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ")"}:</Col>
                <Col>{t("I18N_Description")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{floorAttrs.name}</Col>
                <Col md={3}>{floorAttrs.nameLang2}</Col>
                <Col md={3}>{floorAttrs.nameLang3}</Col>
                <Col md={3}>{floorAttrs.description}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Fl_loc_code")}:</Col>
                <Col>{t("I18N_GFA")}:</Col>
                <Col>{t("I18N_SFA")}:</Col>
                <Col>{t("I18N_Sort_seq_num")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{floorAttrs.floorLocCode}</Col>
                <Col md={3}>{floorAttrs.grossFloorArea}</Col>
                <Col md={3}>{floorAttrs.saleableFloorArea}</Col>
                <Col md={3}>{floorAttrs.sortSequenceNum}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Status")}: </Col>
                <Col></Col>
                <Col></Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{t("I18N_" + floorAttrs.status)}</Col>
                <Col></Col>
                <Col></Col>
                <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{floorAttrs.creatorUsername}</Col>
                <Col>{formatDate(floorAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{floorAttrs.updaterUsername}</Col>
                <Col>{formatDate(floorAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '0px', fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top', marginLeft: '10px' }}>
                {errMsg}
              </span>
            </div>
          </Form.Group>
        </div>


        <LocPickerDialog
          show={showLocPickerDialog}
          handleObjSelect={handleLocSelect}
          onClose={handleLocPickerClose}
          type='FLOOR'
        />

      </Card>
    </>
  );
}

export default FloorAdminDetailsView;
